// Student "Nouvelle demande": pick the academic year (only years the student is
// enrolled in), the document type (with real eligibility) and an optional
// purpose; a blocking rule is shown and prevents submission.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle, Calendar, Info } from 'lucide-react';
import {
  getAvailableTypes,
  checkEligibility,
  createRequest,
  getEnrolledYears,
} from '../../../services/studentRequestsService';
import {
  AttestationTypeOption,
  AttestationType,
  EligibilityCheckResult,
  EnrolledYear,
} from '../../../types/student-requests.types';
import ConfirmDialog from '@/shared/components/ConfirmDialog';

const PURPOSE_MAX_LENGTH = 500;

const apiErrorMessage = (error: unknown, fallback: string): string => {
  const maybe = error as { response?: { data?: { message?: string | string[] } }; message?: string };
  const message = maybe.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message ?? maybe.message ?? fallback;
};

const CreateRequestPage = () => {
  const [years, setYears] = useState<EnrolledYear[]>([]);
  const [selectedYearId, setSelectedYearId] = useState<string | null>(null);
  const [types, setTypes] = useState<AttestationTypeOption[]>([]);
  const [selectedType, setSelectedType] = useState<AttestationType | null>(null);
  const [eligibility, setEligibility] = useState<EligibilityCheckResult | null>(null);
  const [purpose, setPurpose] = useState('');
  const [loading, setLoading] = useState(true);
  const [typesLoading, setTypesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorDialog, setErrorDialog] = useState<{ isOpen: boolean; title: string; message: string }>({
    isOpen: false,
    title: '',
    message: '',
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Extract the requests list path from current URL
  const getRequestsPath = () => {
    const pathParts = location.pathname.split('/');
    const requestsIndex = pathParts.findIndex(part => part === 'requests');
    if (requestsIndex !== -1) {
      return pathParts.slice(0, requestsIndex + 1).join('/');
    }
    return '/student/requests';
  };

  const hasCurrentYear = years.some((y) => y.isCurrent);
  const selectedYear = years.find((y) => y.id === selectedYearId) ?? null;

  // 1. Enrolled years — default to the current one, else the most recent.
  useEffect(() => {
    let cancelled = false;
    const loadYears = async (): Promise<void> => {
      try {
        const data = await getEnrolledYears();
        if (cancelled) return;
        setYears(data);
        const defaultYear = data.find((y) => y.isCurrent) ?? data[0] ?? null;
        setSelectedYearId(defaultYear?.id ?? null);
        if (!defaultYear) setLoading(false);
      } catch (error) {
        console.error('Error loading enrolled years:', error);
        if (!cancelled) setLoading(false);
      }
    };
    void loadYears();
    return () => {
      cancelled = true;
    };
  }, []);

  // 2. Types (with eligibility) for the selected year.
  const loadTypes = useCallback(async (yearId: string): Promise<void> => {
    try {
      setTypesLoading(true);
      const data = await getAvailableTypes(yearId);
      setTypes(data);
    } catch (error) {
      console.error('Error loading types:', error);
      setTypes([]);
    } finally {
      setTypesLoading(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedYearId) return;
    setSelectedType(null);
    setEligibility(null);
    void loadTypes(selectedYearId);
  }, [selectedYearId, loadTypes]);

  // 3. Eligibility detail of the selected type for the selected year.
  useEffect(() => {
    if (!selectedType || !selectedYearId) {
      setEligibility(null);
      return;
    }
    let cancelled = false;
    checkEligibility(selectedType, selectedYearId)
      .then((data) => {
        if (!cancelled) setEligibility(data);
      })
      .catch((error: unknown) => {
        console.error('Error checking eligibility:', error);
        if (!cancelled) setEligibility(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedType, selectedYearId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedType || !selectedYearId) {
      setErrorDialog({
        isOpen: true,
        title: 'Sélection requise',
        message: "Veuillez sélectionner une année académique et un type d'attestation",
      });
      return;
    }

    if (eligibility && !eligibility.eligible) {
      setErrorDialog({
        isOpen: true,
        title: 'Demande impossible',
        message: eligibility.blockingReason ?? "Vous n'êtes pas éligible à ce document",
      });
      return;
    }

    try {
      setSubmitting(true);
      await createRequest({
        requestType: selectedType,
        academicYearId: selectedYearId,
        purpose: purpose.trim() || undefined,
      });
      navigate(getRequestsPath());
    } catch (error) {
      console.error('Error creating request:', error);
      setErrorDialog({
        isOpen: true,
        title: 'Erreur',
        message: apiErrorMessage(error, 'Une erreur est survenue'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-4">Chargement...</p>
        </div>
      </div>
    );
  }

  const canSubmit = !!selectedType && !!selectedYearId && !submitting && (eligibility?.eligible ?? true);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(getRequestsPath())}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Retour
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Nouvelle Demande</h1>
        <p className="text-gray-600 mt-1">Sélectionnez l'année et le type d'attestation que vous souhaitez demander</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Year Selection */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            Année académique
          </h2>
          {years.length === 0 ? (
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Aucune inscription trouvée : vous ne pouvez pas demander de document pour le moment.
                Rapprochez-vous de la scolarité.
              </span>
            </div>
          ) : (
            <>
              <select
                value={selectedYearId ?? ''}
                onChange={(e) => setSelectedYearId(e.target.value)}
                className="w-full md:w-80 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {years.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.name}
                    {y.isCurrent ? ' (année en cours)' : ''}
                    {y.niveauFormation ? ` — Niveau ${y.niveauFormation}` : ''}
                  </option>
                ))}
              </select>
              {!hasCurrentYear && (
                <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                  <Info className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    Vous n'êtes pas inscrit(e) cette année : vous pouvez demander un document pour une année passée.
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Type Selection */}
        {selectedYearId && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Type d'attestation
              {selectedYear ? <span className="text-gray-400 font-normal text-sm"> — {selectedYear.name}</span> : null}
            </h2>
            {typesLoading ? (
              <p className="text-sm text-gray-500">Vérification de votre éligibilité...</p>
            ) : types.length === 0 ? (
              <p className="text-sm text-gray-500">Aucun type de document n'est disponible dans votre établissement.</p>
            ) : (
              <div className="grid gap-3">
                {types.map((type) => (
                  <label
                    key={type.type}
                    className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition ${
                      selectedType === type.type
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${!type.isEligible ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value={type.type}
                      checked={selectedType === type.type}
                      onChange={(e) => setSelectedType(e.target.value as AttestationType)}
                      disabled={!type.isEligible}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{type.labelFr}</div>
                      {type.descriptionFr && (
                        <p className="text-sm text-gray-600 mt-1">{type.descriptionFr}</p>
                      )}
                      {!type.isEligible && type.eligibilityReason && (
                        <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {type.eligibilityReason}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Eligibility Check */}
        {selectedType && eligibility && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Vérification d'éligibilité</h2>

            {!eligibility.eligible && (
              <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{eligibility.blockingReason ?? "Vous n'êtes pas éligible à ce document"}</span>
              </div>
            )}

            <div className="space-y-3">
              {/* Finance Status */}
              <div className="flex items-start gap-3">
                {eligibility.financeAutoApprove ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className={`w-5 h-5 mt-0.5 ${eligibility.financeEligible ? 'text-yellow-600' : 'text-red-600'}`} />
                )}
                <div>
                  <p className="font-medium text-gray-900">Validation Financière</p>
                  <p className="text-sm text-gray-600">{eligibility.financeReason}</p>
                  {eligibility.financeEligible && !eligibility.financeAutoApprove && (
                    <p className="text-xs text-gray-500 mt-1">Une validation manuelle sera requise</p>
                  )}
                </div>
              </div>

              {/* Academic Status */}
              <div className="flex items-start gap-3">
                {eligibility.academicAutoApprove ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <AlertCircle className={`w-5 h-5 mt-0.5 ${eligibility.academicEligible ? 'text-yellow-600' : 'text-red-600'}`} />
                )}
                <div>
                  <p className="font-medium text-gray-900">Validation Académique</p>
                  <p className="text-sm text-gray-600">{eligibility.academicReason}</p>
                  {eligibility.academicEligible && !eligibility.academicAutoApprove && (
                    <p className="text-xs text-gray-500 mt-1">Une validation manuelle sera requise</p>
                  )}
                </div>
              </div>

              {/* Processing Time */}
              {eligibility.eligible && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-900">
                    <strong>Temps de traitement estimé:</strong> {eligibility.estimatedProcessingTime}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Purpose */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Motif (optionnel)</h2>
          <textarea
            value={purpose}
            onChange={(e) => setPurpose(e.target.value.slice(0, PURPOSE_MAX_LENGTH))}
            maxLength={PURPOSE_MAX_LENGTH}
            placeholder="Veuillez préciser la raison de votre demande..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-400 text-right mt-1">{purpose.length}/{PURPOSE_MAX_LENGTH}</p>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate(getRequestsPath())}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Envoi...' : 'Soumettre la demande'}
          </button>
        </div>
      </form>

      {/* Error Dialog */}
      <ConfirmDialog
        isOpen={errorDialog.isOpen}
        title={errorDialog.title}
        message={errorDialog.message}
        confirmText="OK"
        cancelText="Annuler"
        onConfirm={() => setErrorDialog({ ...errorDialog, isOpen: false })}
        onCancel={() => setErrorDialog({ ...errorDialog, isOpen: false })}
        variant="warning"
      />
    </div>
  );
};

export default CreateRequestPage;
